/**
 * Notification Action Types
 *
 * Defines types for handling notification action buttons and user interactions.
 *
 * @see WI-037 Notification Action Handlers
 */

import { Timestamp } from '@angular/fire/firestore';

/**
 * Action types handled by this service (EXHAUSTIVE for Phase 3)
 */
export enum NotificationActionType {
  CHECK_IN = 'check_in',
  SNOOZE = 'snooze',
  VIEW_DETAILS = 'view_details',
  DISMISS = 'dismiss'
}

/**
 * Notification action event metadata
 * Named to avoid collision with NotificationAction from notification-template.types
 */
export interface NotificationActionEvent {
  /** Action type identifier */
  type: NotificationActionType;
  /** Associated check-in ID */
  checkInId: string;
  /** Timestamp when action was triggered */
  timestamp: Timestamp;
  /** Optional action-specific data */
  data?: Record<string, unknown>;
}

/**
 * Result of processing a notification action
 */
export interface ActionResult {
  /** Whether action completed successfully */
  success: boolean;
  /** Action ID that was processed */
  actionId: string;
  /** When the action was processed */
  processedAt: Date;
  /** Result data for successful actions */
  result?: {
    /** Human-readable message */
    message: string;
    /** Optional deep link to navigate to */
    deepLink?: string;
  };
  /** Error details for failed actions */
  error?: {
    /** Error code */
    code: ActionErrorCode;
    /** Error message */
    message: string;
    /** Whether this error is retryable */
    retryable: boolean;
  };
}

/**
 * Error codes for action processing (EXHAUSTIVE)
 */
export type ActionErrorCode =
  | 'INVALID_ACTION'          // Unknown or invalid action type
  | 'INVALID_STATE'           // Check-in state doesn't allow this action
  | 'CHECK_IN_NOT_FOUND'      // Check-in document doesn't exist
  | 'INVALID_STATUS'          // Check-in in invalid status for action
  | 'PERMISSION_DENIED'       // User not authorized for action
  | 'NETWORK_ERROR'           // Network connectivity issue
  | 'TIMEOUT'                 // Operation timed out
  | 'PROCESSING_ERROR'        // General processing error
  | 'VALIDATION_ERROR'        // Action data validation failed
  | 'UNKNOWN_ERROR';          // Unexpected error

/**
 * Action history entry for audit trail
 */
export interface ActionHistoryEntry {
  /** Action that was performed */
  action: NotificationActionEvent;
  /** Result of the action */
  result: ActionResult;
  /** User ID who performed the action */
  userId: string;
}

/**
 * Snooze duration options (in minutes)
 * Matches the preset options in SnoozeDialogComponent
 */
export type SnoozeDuration = 5 | 15 | 30 | 60;

/**
 * Snooze action data
 */
export interface SnoozeActionData {
  /** Duration to snooze in minutes */
  duration: SnoozeDuration;
}

/**
 * Queued action for offline processing
 * Used to persist actions when the user is offline
 */
export interface QueuedAction {
  /** Unique identifier for this queued action */
  id: string;
  /** The action context to be processed */
  action: ActionContext;
  /** When this action was queued */
  createdAt: Date;
  /** Number of processing attempts */
  attempts: number;
  /** Timestamp of last processing attempt */
  lastAttempt: Date | null;
  /** Current processing status */
  status: 'pending' | 'processing' | 'completed' | 'failed';
  /** Error message if processing failed */
  error: string | null;
}

/**
 * Action context passed with each notification action
 */
export interface ActionContext {
  /** Unique identifier for this action */
  actionId: string;
  /** Type of action to perform */
  actionType: NotificationActionType;
  /** Associated notification ID */
  notificationId: string;
  /** Payload data from the notification */
  payload: ActionPayload;
}

/**
 * Payload data from notification
 */
export interface ActionPayload {
  /** Check-in ID to act upon */
  checkInId: string;
  /** User ID who owns the check-in */
  userId: string;
  /** Timestamp when action was triggered */
  timestamp: string;
  /** Platform that triggered the action */
  source: 'ios' | 'android' | 'web';
  /** App state when action was triggered */
  appState: 'foreground' | 'background' | 'terminated';
}

/**
 * Action processor interface for extensibility
 * Allows different action types to have custom validation and processing logic
 */
export interface ActionProcessor {
  /** Check if this processor can handle the given action */
  canProcess(action: ActionContext): boolean;
  /** Validate that the action can be processed */
  validate(action: ActionContext): Promise<boolean>;
  /** Process the action and return a result */
  process(action: ActionContext): Promise<ActionResult>;
}
